import { BreakpointObserver } from '@angular/cdk/layout';

import { Component, computed, effect, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownService } from 'ngx-markdown';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AuthService } from 'src/app/auth.service';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ExportService } from 'src/app/shared/services/export.service';
import { ParamsModalComponent } from 'src/app/shared/services/params-modal/params-modal.component';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ContentService } from 'src/app/views/content/content.service';
import { CreateKnowledgeComponent } from 'src/app/views/content/create-knowledge/create-knowledge.component';
import { CourseDisplayerService } from 'src/app/views/displayer/courses/course-displayer/course-displayer.service';
import { NotesService } from 'src/app/views/displayer/notes/notes.service';
import { PathService } from 'src/app/views/displayer/path/path.service';
import { AppCalendarService } from 'src/app/views/event/app-calendar/app-calendar.service';
import { CalendarConfigComponent } from 'src/app/views/event/app-calendar/calendar-config/calendar-config.component';
import { CalendarFormDialogComponent } from 'src/app/views/event/app-calendar/calendar-form-dialog/calendar-form-dialog.component';
import { TodosConfigComponent } from 'src/app/views/event/todos/todos-config/todos-config.component';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { MySpaceConfigComponent } from 'src/app/views/my-space/my-space-config/my-space-config.component';
import { MySpaceService } from 'src/app/views/my-space/my-space.service';
import * as _ from 'underscore';
import { GenerateContentComponent } from '../../../views/content/generate-content/generate-content.component';
import { SearcherComponent } from '../../../views/content/searcher/searcher.component';
import { GlobalSettingsComponent } from '../../../views/my-space/global-settings/global-settings.component';
import { LectureDialogComponent } from '../../components/lecture-dialog/lecture-dialog.component';
import { SectionDialogComponent } from '../../components/section-dialog/section-dialog.component';
import { HasRoleDirective } from '../../directives/has-role.directive';
import { MaterialModule } from '../../material.module';
import { AddSectionComponent } from '../../services/add-section/add-section.component';
import { ToastService } from '../../services/toast/toast.service';
import { SharedModule } from '../../shared.module';
import { ContentChangeDialogComponent } from '../content-change-dialog/content-change-dialog.component';
import { ContentSearcherComponent } from '../content-searcher/content-searcher.component';
import { CourseArchiveViewerComponent } from '../course-archive-viewer/course-archive-viewer.component';
import { CoursePermissionsComponent } from '../course-permissions/course-permissions.component';
import { CreateUserComponent } from '../create-user/create-user.component';
import { ShareMenuDialogComponent } from '../share-menu-dialog/share-menu-dialog.component';
import { SplitButtonComponent } from '../ui/split-button/split-button.component';
import { SplitButtonMenuItem } from '../ui/split-button/split-button.dto';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { VideoCallStateService } from '../video-call-drawer-chime/video-call-state.service';

class FloatMenuItems {
  'start-call' = false;
  'ai-assistant' = false;
  // 'index-content' = false; // Hidden manual indexing
  'chunk-creator' = false;
  'search' = false;
  'change-views' = false;
  'todo-settings' = false;
  'calendar-settings' = false;
  'my-space-settings' = false;
  'change-room-content' = false;
  'event' = false;
  'course-archives' = false;
  'course-permissions' = false;
  'lecture-links' = false;
  'add-course' = false;
  'add-blog' = false;
  'add-group' = false;
  'edit-user' = false;
  'create-user' = false;
  'language-changer' = false;
  'audio-commands' = false;
  'sync-content' = false;
  'ai-config' = false;
  'copy-content' = false;
  'search-content' = false;
  'add-path' = false;
  'add-note' = false;
  'add-path-content' = false;
  'create-quiz' = false;
  'content-generator' = false;
  'mermaid-generator' = false;
  'image-generator' = false;
  'export-markdown' = false;
  'export-pdf' = false;
  'export-docx' = false;
  'export' = false;
  'import-file' = false;
  'generate-all-audio' = false;

  constructor(items: string[]) {
    for (const item of items) {
      if (this.hasOwnProperty(item)) {
        this[item] = true;
      }
    }
  }
}

export interface FloatMenuItemConfig {
  key: string;
  icon?: string;
  tooltipKey?: string;
  actionKey: string;
  actionParam?: any;
  conditionFn?: () => boolean;
  componentType?: 'button' | 'app-share-button';
  submenu?: FloatMenuItemConfig[];
}

interface CreateButton {
  key: string;
  translateKey: string;
  icon: string;
  func: () => void;
  conditionFn?: () => boolean;
}

@Component({
  selector: 'app-float-edit-menu',
  templateUrl: './float-edit-menu.component.html',
  styleUrls: ['./float-edit-menu.component.scss'],
  standalone: true,
  imports: [SharedModule, MaterialModule, TranslateModule, SplitButtonComponent, HasRoleDirective, GenerateContentComponent],
})
export class FloatEditMenuComponent implements OnInit, OnDestroy {
  public readonly authService = inject(AuthService);
  private chunkService = inject(ChunkService);
  private confirmService = inject(AppConfirmService);
  private markdownService = inject(MarkdownService);
  private exportService = inject(ExportService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  public videoCallStateService = inject(VideoCallStateService);
  @Input() contentID: any;
  @Input() type: any;
  @Input() blogID: any;
  @Input() course: any;
  @Input() blog: any;
  @Input() note: any;
  @Input() noteID: any;
  @Input() isDrawer: any;
  @Input() host: string; // the host component
  @Input() aiAssistantDisplay: 'list' | 'icon' | 'none' = 'list';
  @Input() searchDisplay: 'list' | 'icon' | 'none' = 'list';
  @Input() syncContentDisplay: 'list' | 'icon' | 'none' = 'list';
  @Input() compact: boolean = false;
  @Output() newContentEvent = new EventEmitter<any>();

  private createModeSubscription: Subscription;
  private readModeSubscription: Subscription;
  private editModeSubscription: Subscription;
  private dragModeSubscription: Subscription;
  private coursePermissionMapSubscription: Subscription;
  private userInfoSub: Subscription;
  private courseSubscription: Subscription;
  private routerSubscription: Subscription;

  createMode: boolean;
  readMode: boolean;
  editMode: boolean;
  dragMode: boolean;
  editPermission: any;
  permissionMap = {};
  userInfo: any = {};
  courseID: string;
  lectureID: any;
  sectionID: any;
  floatMenuItems: FloatMenuItems;
  createButton = signal<CreateButton | null>(null);
  isReader = false;
  isSyncing = computed(() => this.contentStateService.loading().status === 'loading');
  isCallInProgress = computed(() => this.videoCallStateService.activeCallId() !== null);

  // Properties for the split button
  showSplitButton = false;
  splitButtonMenuItems: SplitButtonMenuItem[] = [];
  mainSplitButtonAction: SplitButtonMenuItem;

  innerWidth: any;

  below600 = false;
  below992 = false;
  dragDisabled = false;

  public menuItemsConfig: FloatMenuItemConfig[];

  constructor(
    private appService: AppService,
    private dialog: MatDialog,
    private calendarService: AppCalendarService,
    private aroute: ActivatedRoute,
    private route: Router,
    private mySpaceService: MySpaceService,
    private groupService: GroupsService,
    private bottomSheet: MatBottomSheet,
    private breakpointObserver: BreakpointObserver,
    private cdService: CourseDisplayerService,
    private contentService: ContentService,
    private pathService: PathService,
    private noteService: NotesService,
    private contentStateService: ContentStateService
  ) {
    this.routerSubscription = this.route.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd || event instanceof Scroll))
      .subscribe((event: NavigationEnd) => {
        const pathName = event.urlAfterRedirects || event.url || '';
        const pathSegments = pathName?.split('/') || [];

        if (pathName.includes('/knowledge/displayer')) {
          this.courseID = pathSegments[3];
          this.sectionID = pathSegments[4] === 'lecture' ? null : pathSegments[3];
          this.lectureID = pathSegments[5];
          this.setButtonList(this.below600, this.below992, this.createMode);
        }
        this.setCreateButton();
      });

    this.createModeSubscription = appService.createMode$.subscribe((createMode) => {
      this.createMode = createMode;
      this.setButtonList(this.below600, this.below992, this.createMode);
    });

    this.readModeSubscription = appService.readMode$.subscribe((readMode) => {
      // Logic to override readMode if user is a Reader for this content
      const role = this.getUserRole();
      if (role === 'Reader') {
        this.readMode = true;
      } else {
        this.readMode = readMode;
      }
    });
    this.editModeSubscription = appService.editMode$.subscribe((editMode) => (this.editMode = editMode));

    this.dragModeSubscription = appService.dragMode$.subscribe((dragMode) => (this.dragMode = dragMode));

    appService.editPermiission$.subscribe((permission) => {
      this.editPermission = permission;
    });

    this.coursePermissionMapSubscription = mySpaceService.coursePermissions$.subscribe((map) => {
      this.permissionMap = map;
      this.setButtonList(this.below600, this.below992, this.createMode);
    });

    effect(() => {
      this.isReader = this.contentStateService.isReader();
      this.setButtonList(this.below600, this.below992, this.createMode);
    });
  }

  floatMenuIsReadyAndNotEmpty() {
    if (this.floatMenuItems && this.authService.userInfo()?._id) {
      const hasConfigItem = (items: FloatMenuItemConfig[], key: string): boolean => {
        for (const item of items) {
          if (item.key === key) return true;
          if (item.submenu && hasConfigItem(item.submenu, key)) return true;
        }
        return false;
      };

      for (const item in this.floatMenuItems) {
        if (Object.prototype.hasOwnProperty.call(this.floatMenuItems, item) && this.floatMenuItems[item] === true) {
          if (this.menuItemsConfig && hasConfigItem(this.menuItemsConfig, item)) {
            return true;
          }
        }
      }
      return false;
    } else {
      return false;
    }
  }

  ngOnInit() {
    this.initializeMenuItemsConfig();

    this.breakpointObserver.observe(['(max-width: 599px)', '(max-width: 992px)', '(min-width: 599px)', '(min-width: 992px)']).subscribe((result) => {
      if (result.matches) {
        this.below600 = result.breakpoints['(max-width: 599px)'];
        this.below992 = result.breakpoints['(max-width: 992px)'];
        this.setButtonList(this.below600, this.below992, this.createMode);
      }
    });

    this.innerWidth = window.innerWidth;

    if (this.courseID) {
      this.aroute.params.subscribe((params) => {
        if (this.courseSubscription) this.courseSubscription.unsubscribe();
        this.courseSubscription = this.cdService.course$.subscribe((course) => {
          if (course && course.sections) {
            for (const section of course.sections) {
              if (section && section.lectures) {
                for (const lecture of section.lectures) {
                  if (lecture._id === params.lecture) {
                    this.lectureID = lecture._id;
                    this.sectionID = section._id;
                    this.contentID = lecture.content_id;
                  }
                }
              }
            }
          }
        });
      });
    }

    this.userInfoSub = this.authService.userInfo$.subscribe((userInfo) => {
      this.userInfo = userInfo;
    });
    setTimeout(() => {
      this.createMode = JSON.parse(localStorage.getItem('create-mode'));
      this.setButtonList(this.below600, this.below992, this.createMode);
    }, 1500);
  }

  initializeMenuItemsConfig(): void {
    this.menuItemsConfig = [
      { key: 'sync-content', icon: 'sync', tooltipKey: 'Global.HardReload', actionKey: 'syncContent', componentType: 'button' },
      { key: 'chunk-creator', icon: 'keyboard', tooltipKey: 'Global.ShowEditor', actionKey: 'setReadMode', componentType: 'button' },
      { key: 'create-quiz', icon: 'grading', tooltipKey: 'Quiz.CreateTitle', actionKey: 'openQuizGenerator', componentType: 'button' },
      { key: 'content-generator', icon: 'edit_note', tooltipKey: 'ContentGenerator.Title', actionKey: 'openContentGenerator', componentType: 'button' },
      { key: 'mermaid-generator', icon: 'account_tree', tooltipKey: 'MermaidGenerator.Title', actionKey: 'openMermaidGenerator', componentType: 'button' },
      { key: 'image-generator', icon: 'image_inset', tooltipKey: 'ImageGenerator.Title', actionKey: 'openImageGenerator', componentType: 'button' },
      { key: 'ai-assistant', icon: 'smart_toy', tooltipKey: 'Courses.ChunkCreator.GenerateWithAI', actionKey: 'openAIPane', componentType: 'button' },
      { key: 'start-call', icon: 'video_camera_front', tooltipKey: 'Global.VideoChat', actionKey: 'startCall', componentType: 'button' },
      // {
      //   key: 'index-content',
      //   icon: 'database_search',
      //   tooltipKey: 'ChunkDisplayer.chunkOptions.IndexContent',
      //   actionKey: 'generateEmbeddingsForCurrentContent',
      //   componentType: 'button',
      // },
      { key: 'change-views', icon: 'drag_handle', tooltipKey: 'Global.DragChunks', actionKey: 'toggleDragMode', componentType: 'button' },
      { key: 'add-course', icon: 'add', tooltipKey: 'Courses.Overview.CreateCourse', actionKey: 'createCourse', componentType: 'button' },
      // { key: 'add-content', icon: 'add', tooltipKey: 'Global.Create', actionKey: 'createContent', componentType: 'button' },
      { key: 'add-note', icon: 'add', tooltipKey: 'Notes.Create', actionKey: 'createNote', componentType: 'button' },
      { key: 'add-path', icon: 'add', tooltipKey: 'Path.AddPath', actionKey: 'openDialog', actionParam: 'create-path', componentType: 'button' },
      { key: 'add-blog', icon: 'add', tooltipKey: 'Blog.Overview.CreatePost', actionKey: 'openDialog', actionParam: 'create-blog', componentType: 'button' },
      { key: 'add-group', icon: 'add', tooltipKey: 'Global.AddGroup', actionKey: 'createGroup', componentType: 'button' },
      { key: 'todo-settings', icon: 'tune', tooltipKey: 'Global.Settings', actionKey: 'openTodoSettingsMenu', componentType: 'button' },
      { key: 'calendar-settings', icon: 'tune', tooltipKey: 'Global.Settings', actionKey: 'openCalendarSettingsMenu', componentType: 'button' },
      { key: 'my-space-settings', icon: 'list', tooltipKey: 'Global.Settings', actionKey: 'openMySpaceSettingsMenu', componentType: 'button' },
      { key: 'change-room-content', icon: 'search', tooltipKey: 'Global.ChangeContent', actionKey: 'openContentChangeDialog', componentType: 'button' },
      { key: 'event', icon: 'event', tooltipKey: 'Global.CreateEvent', actionKey: 'createEvent', componentType: 'button' },
      { key: 'course-archives', icon: 'archive', tooltipKey: 'Global.Archives', actionKey: 'openCourseArchiveViewer', actionParam: 'course', componentType: 'button' },
      { key: 'blog-archives', icon: 'archive', tooltipKey: 'Global.Archives', actionKey: 'openCourseArchiveViewer', actionParam: 'blog', componentType: 'button' },
      { key: 'course-permissions', icon: 'lock_open', tooltipKey: 'Global.contentAccess', actionKey: 'openCoursePermissionDialog', componentType: 'button' },
      { key: 'search', icon: 'search', tooltipKey: 'Global.search', actionKey: 'openSearchDialog', componentType: 'button' },
      {
        key: 'lecture-links',
        icon: 'link',
        tooltipKey: 'Global.CreateLink',
        actionKey: 'openLinkConfig',
        conditionFn: () => this.type === 'course',
        componentType: 'button',
      },
      { key: 'create-user', icon: 'person_add', tooltipKey: 'CreateUser.CreateUserAccount', actionKey: 'createUser', componentType: 'button' },
      { key: 'ai-config', icon: 'settings', tooltipKey: 'MySpace.AISection', actionKey: 'openAISettings', componentType: 'button' },
      { key: 'edit-user', icon: 'reduce_capacity', tooltipKey: 'Global.ManageAccess', actionKey: 'editUsers', componentType: 'button' },
      { key: 'copy-content', icon: 'content_copy', tooltipKey: 'Courses.ChunkCreator.copyContent', actionKey: 'copyAllContent', componentType: 'button' },
      { key: 'search-content', icon: 'manage_search', actionKey: 'openContentSearcher', componentType: 'button' },
      { key: 'add-path-content', icon: 'add', tooltipKey: 'Global.Add', actionKey: 'openPathContentSearchDialog', componentType: 'button' },
      {
        key: 'export',
        icon: 'file_download',
        tooltipKey: 'Global.Export',
        actionKey: 'noop',
        componentType: 'button',
        submenu: [
          { key: 'export-markdown', icon: 'markdown', tooltipKey: 'Global.ExportMarkdown', actionKey: 'exportMarkdown', componentType: 'button' },
          { key: 'export-pdf', icon: 'picture_as_pdf', tooltipKey: 'Global.ExportPdf', actionKey: 'exportPdf', componentType: 'button' },
          { key: 'export-docx', icon: 'description', tooltipKey: 'Global.ExportDocx', actionKey: 'exportDocx', componentType: 'button' },
        ],
      },
      { key: 'import-file', icon: 'file_upload', tooltipKey: 'Global.ImportFile', actionKey: 'triggerImportFile', componentType: 'button' },
      { key: 'generate-all-audio', icon: 'text_to_speech', tooltipKey: 'ChunkController.Options.GenerateAllAudio', actionKey: 'generateAllAudio', componentType: 'button' },
    ];
  }

  @HostListener('window:resize', [])
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  getFilteredMenuItems(): FloatMenuItemConfig[] {
    return this.menuItemsConfig.filter((config) => {
      if (config.key === 'ai-assistant' && !this.authService.canUseAI()) {
        return false;
      }
      if (
        (config.key === 'content-generator' || config.key === 'create-quiz' || config.key === 'image-generator' || config.key === 'mermaid-generator') &&
        !this.authService.canUseAI()
      ) {
        return false;
      }
      if (config.key === 'ai-assistant' && this.aiAssistantDisplay !== 'list' && !this.below600) {
        return false;
      }
      if (config.key === 'search' && this.searchDisplay !== 'list' && !this.below992) {
        return false;
      }
      if (config.key === 'sync-content' && this.syncContentDisplay !== 'list') {
        return false;
      }

      // For 'export' item, show it if any of its submenu items are enabled, or if 'export' itself is enabled.
      if (config.key === 'export') {
        return !!(this.floatMenuItems?.['export'] || this.floatMenuItems?.['export-markdown'] || this.floatMenuItems?.['export-pdf'] || this.floatMenuItems?.['export-docx']);
      }

      return !!this.floatMenuItems?.[config.key];
    });
  }

  getExportSubmenuItems(): FloatMenuItemConfig[] {
    const exportConfig = this.menuItemsConfig.find((c) => c.key === 'export');
    if (!exportConfig || !exportConfig.submenu) {
      return [];
    }
    return exportConfig.submenu.filter((sub) => this.floatMenuItems?.[sub.key]);
  }

  public menuSections: { titleKey: string; keys: string[] }[] = [
    {
      titleKey: 'FloatMenu.Section.Ai',
      keys: ['ai-assistant', 'content-generator', 'image-generator', 'create-quiz', 'mermaid-generator'],
    },
    // {
    //   titleKey: 'FloatMenu.Section.Content',
    //   keys: ['chunk-creator', 'add-course', 'add-note', 'add-path', 'add-blog', 'add-group', 'event', 'add-path-content', 'change-views'],
    // },
    {
      titleKey: 'FloatMenu.Section.Actions',
      keys: ['search', 'generate-all-audio', 'copy-content', 'search-content', 'sync-content', 'audio-commands'],
    },
    {
      titleKey: 'FloatMenu.Section.Files',
      keys: ['export', 'import-file'],
    },
    {
      titleKey: 'FloatMenu.Section.Communication',
      keys: ['start-call'],
    },
    // {
    //   titleKey: 'FloatMenu.Section.Settings',
    //   keys: [
    //     'lecture-links',
    //     'share-lecture',
    //     'course-permissions',
    //     'course-archives',
    //     'blog-archives',
    //     'todo-settings',
    //     'calendar-settings',
    //     'my-space-settings',
    //     'change-room-content',
    //     'create-user',
    //     'edit-user',
    //     'ai-config',
    //     'language-changer',
    //   ],
    // },
  ];

  getGroupedMenuItems(): { titleKey: string; items: FloatMenuItemConfig[] }[] {
    const filtered = this.getFilteredMenuItems();
    const groups: { titleKey: string; items: FloatMenuItemConfig[] }[] = [];

    for (const section of this.menuSections) {
      const sectionItems = section.keys.map((key) => filtered.find((item) => item.key === key)).filter((item): item is FloatMenuItemConfig => !!item);

      if (sectionItems.length > 0) {
        groups.push({
          titleKey: section.titleKey,
          items: sectionItems,
        });
      }
    }

    // Catch-all for any items not explicitly categorized, just in case
    const categorizedKeys = this.menuSections.flatMap((s) => s.keys);
    const uncategorizedItems = filtered.filter((item) => !categorizedKeys.includes(item.key));
    if (uncategorizedItems.length > 0) {
      groups.push({
        titleKey: '',
        items: uncategorizedItems,
      });
    }

    return groups;
  }

  openShareMenu(): void {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    const data = {
      courseID: this.courseID,
      sectionID: this.sectionID,
      lectureID: this.lectureID,
      blogID: this.blogID,
    };
    if (isSmallScreen) {
      this.bottomSheet.open(ShareMenuDialogComponent, { data });
    } else {
      this.dialog.open(ShareMenuDialogComponent, {
        width: '500px',
        maxHeight: '80vh',
        data,
      });
    }
  }

  generateAllAudio() {
    this.confirmService
      .confirm({
        title: this.translate.instant('ChunkController.Options.GenerateAllAudio'),
        message: this.translate.instant('ChunkController.Options.GenerateAllAudioConfirm'),
      })
      .subscribe((res) => {
        if (res) {
          this.chunkService.generateAllAudios(this.contentID, this.translate.currentLang).subscribe({
            next: () => {
              this.toast.snackbar.open(this.translate.instant('ChunkController.Options.GenerateAllAudioStarted'), 'Ok', {
                duration: 3000,
              });
            },
            error: (err) => {
              console.error(err);
              this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
                duration: 3000,
              });
            },
          });
        }
      });
  }

  handleItemClick(actionKey?: string, actionParam?: any, event?: Event): void {
    if (!actionKey || actionKey === 'noop') return;

    switch (actionKey) {
      case 'setReadMode':
        this.setReadMode();
        break;
      case 'syncContent':
        this.syncContent();
        break;
      case 'startCall':
        this.videoCallStateService.activeCallId.set(this.contentID);
        this.videoCallStateService.toggleDrawer();
        break;
      case 'openAIPane':
        this.openAIPane();
        break;
      // case 'generateEmbeddingsForCurrentContent':
      //   this.contentService.generateEmbeddingsForContentChunks(this.contentID);
      //   break;
      case 'toggleDragMode':
        this.toggleDragMode();
        break;
      case 'createCourse':
        this.createCourse();
        break;
      case 'createContent':
        this.createContent();
        break;
      case 'createNote':
        this.createNote();
        break;
      case 'openDialog':
        this.openDialog(actionParam);
        break;
      case 'createGroup':
        this.createGroup();
        break;
      case 'openTodoSettingsMenu':
        this.openTodoSettingsMenu();
        break;
      case 'openCalendarSettingsMenu':
        this.openCalendarSettingsMenu();
        break;
      case 'openMySpaceSettingsMenu':
        this.openMySpaceSettingsMenu();
        break;
      case 'openContentChangeDialog':
        this.openContentChangeDialog();
        break;
      case 'createEvent':
        this.createEvent();
        break;
      case 'openCourseArchiveViewer':
        this.openCourseArchiveViewer(actionParam);
        break;
      case 'openCoursePermissionDialog':
        this.openCoursePermissionDialog();
        break;
      case 'openSearchDialog':
        this.openSearchDialog();
        break;
      case 'openLinkConfig':
        this.openLinkConfig();
        break;
      case 'createUser':
        this.createUser();
        break;
      case 'openAISettings':
        this.openAISettings();
        break;
      case 'editUsers':
        this.editUsers();
        break;
      case 'copyAllContent':
        this.copyAllContent();
        break;
      case 'openContentSearcher':
        this.openContentSearcher();
        break;
      case 'openPathContentSearchDialog':
        this.openPathContentSearchDialog();
        break;
      case 'openPathContentSearchDialog':
        this.openPathContentSearchDialog();
        break;
      case 'openQuizGenerator':
        if (!this.authService.canUseAI()) return;
        this.contentStateService.openQuizGenerator({});
        break;
      case 'openContentGenerator':
        if (!this.authService.canUseAI()) return;
        this.contentStateService.openContentGenerator();
        break;
      case 'openMermaidGenerator':
        if (!this.authService.canUseAI()) return;
        this.contentStateService.openMermaidGenerator();
        break;
      case 'openImageGenerator':
        if (!this.authService.canUseAI()) return;
        this.contentStateService.openImageGenerator();
        break;
      case 'exportMarkdown':
        this.exportMarkdown();
        break;
      case 'exportPdf':
        this.exportPdf();
        break;
      case 'exportDocx':
        this.exportDocx();
        break;
      case 'triggerImportFile':
        this.triggerImportFile();
        break;
      case 'generateAllAudio':
        this.generateAllAudio();
        break;
      default:
        console.warn('Unknown action key in float menu:', actionKey);
    }
  }

  async setCreateButton(): Promise<void> {
    // Reset state for both button types
    this.createButton.set(null);
    this.showSplitButton = false;

    const currentUrl = this.route.url;

    if (currentUrl.includes('/knowledge/overview') || currentUrl.includes('/knowledge/displayer')) {
      this.showSplitButton = true;
      this.mainSplitButtonAction = {
        id: 'add-lecture',
        text: 'Courses.Overview.AddLecture',
        icon: 'note_add',
        action: () =>
          this.dialog
            .open(LectureDialogComponent, { data: { mode: 'create', courseID: this.courseID, sectionID: this.course.sections[0]?._id }, width: '500px' })
            .afterClosed()
            .subscribe((res) => {
              if (res === 'ok') this.mySpaceService.passBlogListEvent('new-blog');
            }),
      };
      this.splitButtonMenuItems = [
        {
          id: 'add-section',
          text: 'Courses.Overview.AddSection',
          icon: 'create_new_folder',
          action: () =>
            this.dialog
              .open(SectionDialogComponent, { data: { mode: 'create', courseID: this.courseID }, width: '700px', disableClose: true })
              .afterClosed()
              .subscribe((res) => {
                if (res === 'ok') this.mySpaceService.passBlogListEvent('new-blog');
              }),
        },
        {
          id: 'add-lecture',
          text: 'Courses.Overview.AddLecture',
          icon: 'note_add',
          action: () =>
            this.dialog
              .open(LectureDialogComponent, { data: { mode: 'create', courseID: this.courseID, sectionID: this.course.sections[0]?._id }, width: '500px' })
              .afterClosed()
              .subscribe((res) => {
                if (res === 'ok') this.mySpaceService.passBlogListEvent('new-blog');
              }),
        },
      ];
    } else {
      // Logic for regular buttons on other pages
      const items = {
        '/knowledge/list': {
          key: 'add-course',
          translateKey: 'Home.Header.CourseButton',
          icon: 'add',
          func: () => this.createCourse(),
        },
        '/articles/list': {
          key: 'add-blog',
          translateKey: 'Home.Header.BlogButton',
          icon: 'add',
          func: () => this.openDialog('create-blog'),
        },
        '/path/list': {
          key: 'add-path',
          translateKey: 'Home.Header.Path',
          icon: 'add',
          func: () => this.openDialog('create-path'),
        },
        '/path/view': {
          key: 'add-path-content',
          translateKey: 'Global.Add',
          icon: 'add',
          func: () => this.openPathContentSearchDialog(),
        },
        '/notes': {
          key: 'add-note',
          translateKey: 'Home.Header.NoteButton',
          icon: 'add',
          func: () => this.createNote(),
        },
      };

      for (const itemPath in items) {
        if (currentUrl.includes(itemPath)) {
          this.createButton.set(items[itemPath]);
          break;
        }
      }
    }
  }

  onSplitMenuItemClick(itemId: string): void {
    const actions = {
      'add-lecture': () => {
        this.dialog
          .open(LectureDialogComponent, { data: { mode: 'create', courseID: this.courseID, sectionID: this.course.sections[0]?._id }, width: '500px' })
          .afterClosed()
          .subscribe((res) => {
            if (res === 'ok') this.mySpaceService.passBlogListEvent('new-blog');
          });
      },
      'add-section': () => {
        let actualName = this.course?.name;
        if (this.course?.__name?.editions) actualName = this.course.__name.editions[0]?.content || actualName;
        this.dialog
          .open(SectionDialogComponent, { data: { mode: 'create', courseID: this.courseID, courseName: actualName }, width: '700px', disableClose: true })
          .afterClosed()
          .subscribe((res) => {
            if (res === 'ok') this.mySpaceService.passBlogListEvent('new-blog');
          });
      },
    };
    if (actions[itemId]) {
      actions[itemId]();
    }
  }

  async setButtonList(below600: boolean, below992: boolean, editMode: boolean) {
    try {
      const updatedUserData = await this.authService.getUserData(null, true);
      this.userInfo = updatedUserData;
      if (this.blog && this.blog._id) {
        this.blog = await this.contentService.getBlogPost(this.blog._id);
      }
    } catch (err) {
      console.error(err);
    }
    const items = {
      '/knowledge/list': new FloatMenuItems(['add-course', 'course-permissions', 'course-archives']),
      '/notes/': new FloatMenuItems([
        'ai-assistant',
        'mermaid-generator',
        'export-markdown',
        'export-pdf',
        'export-docx',
        'import-file',
        'copy-content',
        'sync-content',
        'start-call',
        // 'index-content',
        'create-quiz',
        'generate-all-audio',
        // 'event',
        'search',
        // 'change-views',
        // 'ai-config',
      ]),
      '/knowledge/overview': new FloatMenuItems(['search']),
      '/knowledge/displayer': new FloatMenuItems([
        'ai-assistant',
        'mermaid-generator',
        'content-generator',
        'image-generator',
        'export-markdown',
        'export-pdf',
        'export-docx',
        'import-file',
        'sync-content',
        'copy-content',
        'start-call',
        // 'index-content',
        'create-quiz',
        'generate-all-audio',
        // 'event',
        'search',
        // 'change-views',
        // 'lecture-links',
        // 'share-lecture',
        // 'ai-config',
      ]),
      '/articles/': new FloatMenuItems([
        'ai-assistant',
        'mermaid-generator',
        'export-markdown',
        'export-pdf',
        'export-docx',
        'import-file',
        'sync-content',
        'copy-content',
        'start-call',
        'start-call',
        'generate-all-audio',
        // 'index-content',
        // 'create-quiz',
        // 'search',
        // 'event',
        // 'ai-config',
        // 'change-views',
        // 'share-lecture',
        // 'audio-commands',
        // 'language-changer',
      ]),
      '/path/list': new FloatMenuItems(['add-path']),
      '/path/view': new FloatMenuItems(['add-path-content']),
      '/articles/list': new FloatMenuItems(['add-blog', 'search', 'blog-archives']),
      '/myspace/profile': new FloatMenuItems([]),
      '/myspace/upload-app-image': new FloatMenuItems([]),
      '/myspace/account': new FloatMenuItems([]),
      '/myspace/personalize-app': new FloatMenuItems([]),
      '/myspace/myjourney': new FloatMenuItems([]),
      '/myspace/requests': new FloatMenuItems([]),
      '/myspace/credit': new FloatMenuItems([]),
      '/myspace/my-models': new FloatMenuItems([]),
      '/myspace/ai-section': new FloatMenuItems([]),
      '/myspace/user-management': new FloatMenuItems([]),
      '/groups': new FloatMenuItems(['add-group', 'search']),
      '/calendar': new FloatMenuItems([]),
      '/todos': new FloatMenuItems([]),
      '/search': new FloatMenuItems(['ai-config']),
      '/questions': new FloatMenuItems([]),
      '/rooms/:id': new FloatMenuItems(['add-group']),
    };

    const adminItems = ['edit-user', 'create-user'];
    const writerItems = ['add-course', 'add-blog', 'lecture-links', 'change-views', 'generate-all-audio'];
    const editItems = ['edit-user', 'create-user', 'add-course', 'add-blog', 'add-group', 'lecture-links', 'change-views'];

    let currentRouteItems: FloatMenuItems | undefined;
    for (const itemPath in items) {
      if (this.route.url.includes(itemPath)) {
        currentRouteItems = items[itemPath];
        break;
      }
    }
    this.floatMenuItems = currentRouteItems || new FloatMenuItems([]);

    const setFloatMenuItems = (newItems: string[], value: boolean) => {
      if (!this.floatMenuItems) {
        return;
      }
      for (const newItem of newItems) {
        if (this.floatMenuItems.hasOwnProperty(newItem)) {
          this.floatMenuItems[newItem] = value;
        }
      }
    };

    // Enable content actions by default if they are present in the current route's menu items
    // They will be disabled later if the user is a Reader
    const contentActions = [/*'index-content', */ 'start-call', 'content-generator', 'mermaid-generator', 'image-generator'];
    setFloatMenuItems(contentActions, true);

    if (!this.authService.isWriter()) {
      // If not dev, start by disabling admin and writer specific items
      setFloatMenuItems(_.union(writerItems, adminItems), false);

      if (this.authService.isAdmin()) {
        // Admin gets everything
        setFloatMenuItems(_.union(writerItems, adminItems), true);
      } else {
        // Writer gets writer items
        setFloatMenuItems(writerItems, true);
      }
    } else if (!this.authService.isAdmin()) {
      // Writer but not admin (unlikely based on isWriter logic but keeping for safety)
      setFloatMenuItems(adminItems, false);
    }

    if (!editMode) {
      setFloatMenuItems(editItems, false);
    }
    if (!below992) {
      setFloatMenuItems(['language-changer'], false);
    } else {
      setFloatMenuItems(['language-changer'], true);
    }

    if (below992) {
      if (this.route.url.includes('/todos')) {
        setFloatMenuItems(['todo-settings'], true);
      }
      if (this.route.url.includes('/calendar')) {
        setFloatMenuItems(['calendar-settings'], true);
      }
      if (this.route.url.includes('/articles/') && !editMode) {
        setFloatMenuItems(['audio-commands'], true);
      }
      if (this.route.url.includes('/myspace')) {
        setFloatMenuItems(['my-space-settings'], true);
      }
    }

    if (below992) {
      if (this.type === 'blog') {
        if (this.createMode) {
          setFloatMenuItems(['audio-commands'], false);
        }
      }
    } else {
      if (this.type === 'blog') {
        setFloatMenuItems(['audio-commands'], false);
      }
    }

    this.isReader = this.contentStateService.isReader();

    if (this.isReader) {
      this.readMode = true; // Force readMode for Readers
      setFloatMenuItems(
        [
          'start-call',
          'content-generator',
          'mermaid-generator',
          'image-generator',
          'create-quiz',
          'generate-all-audio',
          'export',
          'export-markdown',
          'export-pdf',
          'export-docx',
          'import-file',
        ],
        false
      );
    }
  }

  openCoursePermissionDialog() {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    if (isSmallScreen) {
      this.bottomSheet.open(CoursePermissionsComponent);
    } else {
      this.dialog.open(CoursePermissionsComponent, {
        width: '500px',
        maxHeight: '80vh',
        autoFocus: false,
      });
    }
  }
  openCourseArchiveViewer(type: string) {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    if (isSmallScreen) {
      this.bottomSheet.open(CourseArchiveViewerComponent, { data: { type } });
    } else {
      this.dialog.open(CourseArchiveViewerComponent, {
        width: '500px',
        maxHeight: '80vh',
        data: { type },
      });
    }
  }
  createUser() {
    this.dialog.open(CreateUserComponent, { width: '500px', autoFocus: false });
  }

  createCourse() {
    this.dialog
      .open(CreateKnowledgeComponent, {
        width: '90vw',
        maxWidth: '800px',
        autoFocus: false,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.mySpaceService.passCourseListEvent('new-course');
        }
      });
  }
  createGroup() {
    this.dialog
      .open(AddSectionComponent, {
        data: { type: 'create-group' },
        width: '500px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        this.groupService.passGroupEvent('new-group');
      });
  }

  createContent() {
    this.dialog.open(AddSectionComponent, {
      data: { type: 'choose-content-type' },
      width: '500px',
      autoFocus: false,
    });
  }

  openDialog(type: string, courseID: string | null = null) {
    this.dialog
      .open(AddSectionComponent, {
        data: { type, courseID },
        width: '500px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.mySpaceService.passBlogListEvent('new-blog');
        }
      });
  }
  onCourseList() {
    return this.route.url === '/knowledge/list';
  }
  onBlogList() {
    return this.route.url === '/articles/list';
  }

  canBeDisplayed() {
    if (this.route.url === '/calendar' || this.route.url === '/todos') {
      if (this.innerWidth <= 992) {
        return true;
      }
    }
    return this.route.url !== '/calendar' && this.route.url !== '/todos' && !this.route.url.includes('/rooms');
  }

  editUsers() {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    if (isSmallScreen) {
      this.bottomSheet.open(UserEditComponent);
    } else {
      this.dialog.open(UserEditComponent, {
        width: '500px',
        maxHeight: '80vh',
        autoFocus: false,
      });
    }
  }

  hasWrittingRights() {
    if (this.type === 'blog') {
      return true;
    }
    if (
      this.permissionMap &&
      this.permissionMap[this.courseID] &&
      this.permissionMap[this.courseID][this.sectionID] &&
      this.permissionMap[this.courseID][this.sectionID][this.lectureID]
    ) {
      const currentPermissions = this.permissionMap[this.courseID][this.sectionID][this.lectureID];
      return currentPermissions.role === 'Writer' || currentPermissions.role === 'Admin';
    }
    return false; // Added default return
  }

  hasCourseWritingRights(): boolean {
    if (!this.permissionMap || !this.permissionMap[this.courseID]) {
      return false;
    }
    const coursePermissions = this.permissionMap[this.courseID];
    return coursePermissions.role === 'Writer' || coursePermissions.role === 'Admin';
  }

  getUserRole() {
    if (this.type === 'blog') {
      if (this.blog && this.userInfo) {
        const userID = this.userInfo.id || this.authService.getID();
        const userEntry = this.blog.users.find((e) => e._id === userID) || {};
        return userEntry.role;
      }
    }
    if (
      this.permissionMap &&
      this.permissionMap[this.courseID] &&
      this.permissionMap[this.courseID][this.sectionID] &&
      this.permissionMap[this.courseID][this.sectionID][this.lectureID]
    ) {
      const currentPermissions = this.permissionMap[this.courseID][this.sectionID][this.lectureID];
      return currentPermissions.role;
    }
    if (this.type === 'note' && this.note && this.authService.userInfo()) {
      const role = this.note.creator === this.authService.userInfo()._id ? 'Admin' : 'Reader';
      return role;
    }
    return undefined; // Added default return
  }

  setReadMode() {
    if (!this.readMode) {
      this.appService.setEditMode(false);
    }
    this.appService.setReadMode(!this.readMode);
    this.contentService.setInsertBelowChunk(null);
  }

  openLinkConfig() {
    this.dialog.open(ParamsModalComponent, {
      data: { contentID: this.contentID },
      minWidth: '70%',
      autoFocus: false,
    });
  }

  async createNote(): Promise<void> {
    const newNote = await this.noteService.createNewNote();
    await this.route.navigate(['/notes', newNote.id]);
  }

  openSearchDialog() {
    if (this.route.url.includes('/knowledge/displayer') || this.route.url.includes('/notes/')) {
      this.contentStateService.openQuestionsSearchSidebar(true);
      return;
    }
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    let invoker;
    if (isSmallScreen) {
      container = this.bottomSheet;
      invoker = 'bottom-sheet';
    } else {
      container = this.dialog;
      invoker = 'dialog';
    }
    const ref = container.open(SearcherComponent, {
      data: { container: invoker },
      maxHeight: '80vh',
    });
    const onCloseSub = ref.afterClosed ? ref.afterClosed() : ref.afterDismissed();
    onCloseSub.subscribe((res) => {});
  }

  createEvent() {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    if (isSmallScreen) {
      container = this.bottomSheet;
    } else {
      container = this.dialog;
    }
    const ref = container.open(CalendarFormDialogComponent, {
      data: {
        contentID: this.contentID,
        action: 'add',
        date: new Date(),
      },
      maxHeight: '80vh',
    });
    const onCloseSub = ref.afterClosed ? ref.afterClosed() : ref.afterDismissed();
    onCloseSub.subscribe((res) => {
      if (!res) {
        return;
      }
      const responseEvent = res.event;
      this.calendarService.addEvent(responseEvent).subscribe((events) => {});
    });
  }

  openContentChangeDialog() {
    this.dialog
      .open(ContentChangeDialogComponent, {
        height: '50%',
        width: '75%',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res && res.course && res.course._id) {
          // Added null check for res and res.course
          this.newContentEvent.emit(res);
        }
      });
  }

  syncContent() {
    this.contentStateService.getPieces(true, true);
  }

  openAIPane() {
    if (!this.authService.canUseAI()) return;
    this.contentStateService.openAIPane({ chunkAboveId: null, chunkBelowId: null });
  }

  toggleMode() {
    this.appService.setEditMode(!this.editMode);
  }

  toggleDragMode() {
    if (this.dragMode) {
      this.contentService.setInsertBelowChunk(null);
      this.appService.activateDragMode(false);
    } else {
      this.appService.activateDragMode(true);
    }
  }

  openTodoSettingsMenu() {
    this.bottomSheet.open(TodosConfigComponent);
  }

  openCalendarSettingsMenu() {
    this.bottomSheet.open(CalendarConfigComponent);
  }

  openMySpaceSettingsMenu() {
    this.bottomSheet.open(MySpaceConfigComponent);
  }

  openAISettings() {
    const isSmallScreen = this.breakpointObserver.isMatched('(max-width: 599px)');
    let container;
    if (isSmallScreen) {
      container = this.bottomSheet;
    } else {
      container = this.dialog;
    }
    container.open(GlobalSettingsComponent, {
      data: {},
      maxHeight: '80vh',
    });
  }
  async copyAllContent() {
    const output = await this.contentService.getAllContent(this.contentID);
    this.appService.copyHTMLToClipBoard(output);
  }

  async exportMarkdown() {
    if (!this.contentID) return;
    const downloadId = Math.random().toString(36).substring(2, 15);
    this.exportService.exportStatus.set({ state: 'generating', type: 'markdown', contentId: this.contentID, downloadId });
    try {
      await firstValueFrom(this.exportService.exportMarkdown(this.contentID, this.translate.currentLang, undefined, downloadId));
    } catch (err) {
      console.error('Error exporting markdown:', err);
      this.exportService.exportStatus.set({ state: 'error', type: 'markdown' });
    }
  }

  async exportPdf() {
    if (!this.contentID) return;
    const downloadId = Math.random().toString(36).substring(2, 15);
    this.exportService.exportStatus.set({ state: 'generating', type: 'pdf', contentId: this.contentID, downloadId });
    try {
      await firstValueFrom(this.exportService.exportPdf(this.contentID, this.translate.currentLang, undefined, downloadId));
    } catch (err) {
      console.error('Error exporting pdf:', err);
      this.exportService.exportStatus.set({ state: 'error', type: 'pdf' });
    }
  }

  async exportDocx() {
    if (!this.contentID) return;
    const downloadId = Math.random().toString(36).substring(2, 15);
    this.exportService.exportStatus.set({ state: 'generating', type: 'docx', contentId: this.contentID, downloadId });
    try {
      await firstValueFrom(this.exportService.exportDocx(this.contentID, this.translate.currentLang, undefined, downloadId));
    } catch (err) {
      console.error('Error exporting docx:', err);
      this.exportService.exportStatus.set({ state: 'error', type: 'docx' });
    }
  }

  triggerImportFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,.txt';
    input.onchange = (event: any) => this.onFileImportSelected(event);
    input.click();
  }

  onFileImportSelected(event: any) {
    const file = event.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const text = e.target?.result || '';
      this.openCanvasWithImportedContent(file.name, text);
    };
    reader.readAsText(file);
  }

  async openCanvasWithImportedContent(fileName: string, text: string) {
    let htmlContent: any = '';
    try {
      const parsed = this.markdownService.parse(text);
      if (parsed instanceof Promise) {
        htmlContent = await parsed;
      } else {
        htmlContent = parsed;
      }
    } catch (err) {
      console.error('Error parsing markdown, using plain text fallback:', err);
      htmlContent = text
        .split('\n')
        .map((para) => `<p>${para}</p>`)
        .join('');
    }

    this.contentStateService.openCanvas({
      title: fileName.replace(/\.[^/.]+$/, ''), // Strip extension
      html: htmlContent,
      contentId: this.contentID,
      source: 'import',
    });
  }
  openContentSearcher() {
    this.dialog.open(ContentSearcherComponent, { width: '50%' });
  }
  async openPathContentSearchDialog() {
    const pathID = this.aroute.snapshot.paramMap.get('id');
    if (pathID) {
      // Added null check for pathID
      const path = await firstValueFrom(this.pathService.getPath(pathID));
      if (path) {
        // Added null check for path
        this.dialog
          .open(ContentSearcherComponent, {
            width: '50%',
            data: { exclude: path.content },
          })
          .afterClosed()
          .subscribe((res) => {
            if (res) {
              this.pathService.addContentToPath({ id: pathID, content: res }).subscribe(() => {
                // Changed res to empty arrow func
                this.pathService.emitPathEvent();
              });
            }
          });
      }
    }
  }

  getMenuItemLabel(item: FloatMenuItemConfig): string {
    if (item.key === 'start-call' && this.isCallInProgress()) {
      return 'Global.VideoChatInProgress';
    }
    return item.tooltipKey || '';
  }

  getMenuItemIcon(item: FloatMenuItemConfig): string {
    if (item.key === 'start-call' && this.isCallInProgress()) {
      return 'close';
    }
    return item.icon || '';
  }

  ngOnDestroy() {
    this.createModeSubscription?.unsubscribe();
    this.readModeSubscription?.unsubscribe();
    this.editModeSubscription?.unsubscribe();
    this.dragModeSubscription?.unsubscribe();
    this.coursePermissionMapSubscription?.unsubscribe();
    this.userInfoSub?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.courseSubscription?.unsubscribe();
  }
}
